import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";

import ChatInterview from "../components/ChatInterview";
import VoiceAgent from "../components/VoiceAgent";
import api from "../lib/api";
import { LANG_CODES, langName } from "../i18n/languages";
import { MessageSquare, Mic } from "lucide-react";

const WEEKDAYS = [
  { k: "monday", l: "Lundi" }, { k: "tuesday", l: "Mardi" },
  { k: "wednesday", l: "Mercredi" }, { k: "thursday", l: "Jeudi" },
  { k: "friday", l: "Vendredi" }, { k: "saturday", l: "Samedi" }, { k: "sunday", l: "Dimanche" },
];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [ref, setRef] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "",
    pharmacy_name: user?.pharmacy_name || "",
    address: user?.address || "",
    phone: user?.phone || "",
    professional_role: user?.professional_role || "Pharmacien",
    experience_years: user?.experience_years || 0,
    bio: user?.bio || "",
    skills: user?.skills || [],
    hourly_rate: user?.hourly_rate || 0,
    available_weekdays: user?.available_weekdays || [],
    available_periods: user?.available_periods || [],
    max_distance_km: user?.max_distance_km || 30,
    permis_b: !!user?.permis_b,
    vehicle: !!user?.vehicle,
    housing_needed: !!user?.housing_needed,
    softwares: user?.softwares || [],
    languages: user?.languages || ["fr"],
    expected_salary: user?.expected_salary || "",
    contract_preferences: user?.contract_preferences || [],
    works_saturday: !!user?.works_saturday,
    works_sunday: !!user?.works_sunday,
    does_guards: !!user?.does_guards,
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/reference").then(({ data }) => setRef(data)); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleIn = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const save = async () => {
    await api.patch("/auth/profile", {
      ...form,
      experience_years: parseInt(form.experience_years) || 0,
      hourly_rate: parseFloat(form.hourly_rate) || 0,
      max_distance_km: parseFloat(form.max_distance_km) || 0,
      expected_salary: form.expected_salary === "" ? null : parseFloat(form.expected_salary),
    });
    await refreshUser();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const isPharmacy = user?.role === "pharmacy";
  const [chatOpen, setChatOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen pb-24 lg:pb-0">
      {isPharmacy ? <Sidebar /> : null}
      <main className="flex-1 min-w-0">
        <div className="max-w-3xl mx-auto p-6 lg:p-10">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
                {isPharmacy ? t("profile.title_pharmacy") : t("profile.title_replacement")}
              </h1>
              <p className="text-sm text-[#6B726A]">{t("profile.subtitle")}</p>
            </div>
            {!isPharmacy && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setVoiceOpen(true)} data-testid="open-profile-voice"
                  className="rp-btn-secondary inline-flex items-center gap-2 text-sm whitespace-nowrap">
                  <Mic className="w-4 h-4" /> {t("profile.speak_voice")}
                </button>
                <button onClick={() => setChatOpen(true)} data-testid="open-profile-interview"
                  className="rp-btn-secondary inline-flex items-center gap-2 text-sm whitespace-nowrap">
                  <MessageSquare className="w-4 h-4" /> {t("profile.complete_chat")}
                </button>
              </div>
            )}
          </div>
          <div className="h-6" />

          <div className="rp-card p-6 sm:p-8 space-y-4">
            <Field label="Nom complet">
              <input className="rp-input" data-testid="profile-name" value={form.name} onChange={upd("name")} />
            </Field>
            <Field label="Téléphone">
              <input className="rp-input" value={form.phone} onChange={upd("phone")} />
            </Field>
            {isPharmacy ? (
              <>
                <Field label="Nom de l'officine">
                  <input className="rp-input" value={form.pharmacy_name} onChange={upd("pharmacy_name")} />
                </Field>
                <Field label="Adresse">
                  <input className="rp-input" value={form.address} onChange={upd("address")} />
                </Field>
              </>
            ) : (
              <>
                <Field label="Fonction">
                  <select className="rp-input" value={form.professional_role} onChange={upd("professional_role")} data-testid="profile-role">
                    {(ref?.professional_roles || ["Pharmacien"]).map((r) => <option key={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Années d'expérience">
                  <input type="number" min={0} className="rp-input" value={form.experience_years} onChange={upd("experience_years")} />
                </Field>
                <Field label="Compétences">
                  <div className="flex flex-wrap gap-2">
                    {(ref?.skills || []).map((s) => (
                      <button key={s} type="button" onClick={() => toggleIn("skills", s)}
                        data-testid={`prof-skill-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          form.skills.includes(s)
                            ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                            : "border-[#E2E5E0] text-[#6B726A]"
                        }`}>{s}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Logiciels métier maîtrisés">
                  <div className="flex flex-wrap gap-2">
                    {(ref?.pharmacy_softwares || []).map((s) => (
                      <button key={s} type="button" onClick={() => toggleIn("softwares", s)}
                        data-testid={`prof-sw-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          form.softwares.includes(s)
                            ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                            : "border-[#E2E5E0] text-[#6B726A]"
                        }`}>{s}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Langues parlées">
                  <div className="flex flex-wrap gap-2" data-testid="prof-languages">
                    {LANG_CODES.map((c) => (
                      <button key={c} type="button" onClick={() => toggleIn("languages", c)}
                        data-testid={`prof-lang-${c}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          form.languages.includes(c)
                            ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                            : "border-[#E2E5E0] text-[#6B726A]"
                        }`}>{langName(c, i18n.language)}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Jours disponibles">
                  <div className="flex flex-wrap gap-2" data-testid="prof-weekdays">
                    {WEEKDAYS.map((w) => (
                      <button key={w.k} type="button" onClick={() => toggleIn("available_weekdays", w.k)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          form.available_weekdays.includes(w.k)
                            ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                            : "border-[#E2E5E0] text-[#6B726A]"
                        }`}>{w.l}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Plages horaires possibles">
                  <div className="flex flex-wrap gap-2">
                    {(ref?.periods || []).map((p) => (
                      <button key={p} type="button" onClick={() => toggleIn("available_periods", p)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          form.available_periods.includes(p)
                            ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                            : "border-[#E2E5E0] text-[#6B726A]"
                        }`}>{p}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Mobilité">
                  <div className="grid sm:grid-cols-2 gap-2">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F7F8F6]">
                      <input type="checkbox" checked={form.permis_b} onChange={(e) => setForm({ ...form, permis_b: e.target.checked })} className="accent-[#1A4D2E]" data-testid="prof-permis" />
                      Permis B
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F7F8F6]">
                      <input type="checkbox" checked={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.checked })} className="accent-[#1A4D2E]" data-testid="prof-vehicle" />
                      Véhicule personnel
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F7F8F6]">
                      <input type="checkbox" checked={form.housing_needed} onChange={(e) => setForm({ ...form, housing_needed: e.target.checked })} className="accent-[#1A4D2E]" />
                      Logement souhaité
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F7F8F6]">
                      <input type="checkbox" checked={form.does_guards} onChange={(e) => setForm({ ...form, does_guards: e.target.checked })} className="accent-[#1A4D2E]" />
                      Disponible pour les gardes
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F7F8F6]">
                      <input type="checkbox" checked={form.works_saturday} onChange={(e) => setForm({ ...form, works_saturday: e.target.checked })} className="accent-[#1A4D2E]" />
                      Travaille le samedi
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F7F8F6]">
                      <input type="checkbox" checked={form.works_sunday} onChange={(e) => setForm({ ...form, works_sunday: e.target.checked })} className="accent-[#1A4D2E]" />
                      Travaille le dimanche
                    </label>
                  </div>
                </Field>

                <Field label="Distance max acceptée (km)">
                  <input type="number" min={0} className="rp-input" value={form.max_distance_km} onChange={upd("max_distance_km")} data-testid="prof-max-distance" />
                </Field>

                <Field label="Types de contrats recherchés">
                  <div className="flex flex-wrap gap-2">
                    {(ref?.contract_types || []).map((s) => (
                      <button key={s} type="button" onClick={() => toggleIn("contract_preferences", s)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          form.contract_preferences.includes(s)
                            ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                            : "border-[#E2E5E0] text-[#6B726A]"
                        }`}>{s}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Bio">
                  <textarea rows={4} className="rp-input resize-none" value={form.bio} onChange={upd("bio")} />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Tarif horaire (€/h)">
                    <input type="number" min={0} className="rp-input" value={form.hourly_rate} onChange={upd("hourly_rate")} />
                  </Field>
                  <Field label="Salaire mensuel attendu (€)">
                    <input type="number" min={0} className="rp-input" value={form.expected_salary} onChange={upd("expected_salary")} />
                  </Field>
                </div>
              </>
            )}

            <div className="flex items-center justify-end gap-3 pt-4">
              {saved && <span className="text-sm text-[#1A4D2E]">✓ {t("common.saved")}</span>}
              <button onClick={save} data-testid="save-profile" className="rp-btn-primary">{t("common.save")}</button>
            </div>
          </div>
        </div>
      </main>
      <ChatInterview
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        title="Construisons votre profil"
        introMessage="Bonjour ! Je vais vous poser quelques questions pour bâtir un profil qui se démarque auprès des pharmacies. 5 minutes max ⏱️"
        submitLabel="Enregistrer mon profil"
        questions={[
          { key: "name", type: "text", question: "Quel est votre nom complet ?", placeholder: "Prénom Nom" },
          { key: "professional_role", type: "choice", question: "Quelle est votre fonction principale ?", options: ["Pharmacien", "Adjoint", "Préparateur"] },
          { key: "experience_years", type: "number", question: "Combien d'années d'expérience avez-vous en officine ?", placeholder: "5" },
          { key: "address", type: "text", question: "Dans quelle ville/quartier êtes-vous basé(e) ?", placeholder: "Paris 11e" },
          { key: "phone", type: "text", question: "Quel est votre numéro de téléphone pro ?", placeholder: "+33 6 12 34 56 78", skippable: true },
          { key: "skills", type: "multi", question: "Quelles sont vos compétences clés ? (choisissez tout ce qui s'applique)", options: ["Vaccination","TROD","Entretiens","Orthopédie","Homéopathie","Aromathérapie","PUI"], skippable: true },
          { key: "hourly_rate", type: "number", question: "Quel est votre tarif horaire souhaité ? (en €/h)", placeholder: "30" },
          { key: "bio", type: "text", question: "Décrivez-vous en une phrase aux pharmacies — qu'est-ce qui vous rend unique ?", placeholder: "Pharmacien adjoint, 5 ans d'expérience, spécialiste vaccination…" },
        ]}
        onSubmit={async (a) => {
          await api.patch("/auth/profile", {
            name: a.name,
            professional_role: a.professional_role,
            experience_years: parseInt(a.experience_years) || 0,
            address: a.address,
            phone: a.phone || "",
            skills: a.skills || [],
            hourly_rate: parseFloat(a.hourly_rate) || 0,
            bio: a.bio || "",
          });
          await refreshUser();
          window.location.reload();
        }}
      />
      <VoiceAgent
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        context="profile"
        onCompleted={async () => { await refreshUser(); window.location.reload(); }}
      />
      <style>{`.rp-input{width:100%;background:#fff;border:1px solid #E2E5E0;border-radius:.75rem;padding:.6rem .9rem;outline:none}.rp-input:focus{border-color:#1A4D2E;box-shadow:0 0 0 3px rgba(26,77,46,.12)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
